import {
  apiUrl,
  getList,
  getListUrl,
  getSingleRecord,
  upload,
  saveRecord,
  deleteRecord,
  download,
  get,
  getAll,
  postAction,
  exportData,
  putAction,
} from './apiUtils';
import type { ApiListParamsWithUnderscores, ApiExportParams } from '../types/api/listParams';
import type {
  ActivatedTransactionModel,
  SoldTransactionModel,
  IngestedTransactionModel,
  MoveTransactionModel,
} from '../types/api/keyCodes';
import type { DisabledTransaction } from '../types/disabledTransaction';
import type { ListApiResponse } from '../types/apiResponse';

const baseUrl = apiUrl + '/keycodes/';
const exportBaseUrl = `${apiUrl}/export/keycodes/`;
const activatedBaseUrl = `${baseUrl}activated/`;
const disabledBaseUrl = `${baseUrl}disabled/`;
const disableReasonsBaseUrl = `${baseUrl}disablereasons/`;
const soldBaseUrl = `${baseUrl}sold/`;
const moveBaseUrl = `${baseUrl}move/`;
const ingestedBaseUrl = `${baseUrl}ingested/`;
const bulkIngestedBaseUrl = `${ingestedBaseUrl}bulk/`;

// ===================================
// Search
export function searchCode(
  params: ApiListParamsWithUnderscores & { code?: string }
): Promise<ListApiResponse<unknown>> {
  const { code, filters = {}, _scopes, _sort, _order, _page, _limit } = params;
  const f: Record<string, string> = { ...filters };
  if (code !== undefined) f.code = code;
  return getList(`${baseUrl}search/code`, { filters: f, _scopes, _sort, _order, _page, _limit });
}

export function searchCodes(
  params: ApiListParamsWithUnderscores & { codesText?: string }
): Promise<ListApiResponse<unknown>> {
  const { codesText, _scopes, _sort, _order, _page, _limit } = params;
  const url = getListUrl(`${baseUrl}search/codes`, {
    _scopes,
    _sort,
    _order,
    _page,
    _limit,
  });
  return postAction(url, { codesText });
}

export function searchTag(
  params: ApiListParamsWithUnderscores & { tag?: string }
): Promise<ListApiResponse<unknown>> {
  const { tag, filters = {}, _scopes, _sort, _order, _page, _limit } = params;
  const f: Record<string, string> = { ...filters };
  if (tag !== undefined) f.tag = tag;
  return getList(`${baseUrl}search/tag`, { filters: f, _scopes, _sort, _order, _page, _limit });
}

export function getCodeStatusHistory(id: string): Promise<unknown> {
  return get(`${baseUrl}${id}/history`);
}

// ===================================
// Activated
export function getActivatedTransactions(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(activatedBaseUrl, params);
}

export function loadActivatedTransactionSkus(
  id: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${activatedBaseUrl}${id}/skus`, params);
}

export function getActivatedTransactionById(id: string): Promise<ActivatedTransactionModel> {
  return getSingleRecord<ActivatedTransactionModel>(activatedBaseUrl, id);
}

export function uploadActivations(record: unknown): Promise<unknown> {
  return upload(activatedBaseUrl, record);
}

export function exportActivatedTransactionData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}activated`, 'creationDate', params);
}

// ===================================
// Disabled
export function getDisabledTransactions(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(disabledBaseUrl, params);
}

export function loadDisabledTransactionSkus(
  id: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${disabledBaseUrl}${id}/skus`, params);
}

export function getDisabledTransactionById(id: string): Promise<DisabledTransaction> {
  return getSingleRecord<DisabledTransaction>(disabledBaseUrl, id);
}

export function uploadDisabled(record: unknown): Promise<unknown> {
  return upload(disabledBaseUrl, record);
}

export function downloadDisabledKeys(id: string): Promise<void> {
  return download(`${disabledBaseUrl}${id}/downloadkeys`);
}

export function downloadDisabledKeysForSkuAndCompany(
  id: string,
  skuId: string,
  companyId: string
): Promise<void> {
  return download(`${disabledBaseUrl}${id}/${skuId}/${companyId}/downloadkeys`);
}

export function authoriseDisabledTransaction(id: string): Promise<unknown> {
  return get(`${disabledBaseUrl}${id}/authorise`);
}

export function exportDisabledTransactionData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}disabled`, 'creationDate', params);
}

// ===================================
// Disabled reasons
export function getAllDisableReasons(): Promise<unknown> {
  return getAll(disableReasonsBaseUrl);
}

export function getDisableReasons(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(disableReasonsBaseUrl, params);
}

export function getDisableReasonById(id: string): Promise<unknown> {
  return getSingleRecord(disableReasonsBaseUrl, id);
}

export function setSteamKeysAsDisabled(id: string): Promise<unknown> {
  return putAction(`${disabledBaseUrl}${id}/markSteamKeysAsDisabled`);
}

export function saveDisableReason(disableReason: unknown): Promise<unknown> {
  return saveRecord(disableReasonsBaseUrl, disableReason);
}

export function deleteDisableReason(id: string): Promise<unknown> {
  return deleteRecord(disableReasonsBaseUrl, id);
}

export function exportDisableReasonsData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}disableReasons`, 'reason', params);
}

// ===================================
// Sold
export function getSoldTransactions(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(soldBaseUrl, params);
}

export function getSoldTransactionById(id: string): Promise<SoldTransactionModel> {
  return getSingleRecord<SoldTransactionModel>(soldBaseUrl, id);
}
export function uploadSold(record: unknown): Promise<unknown> {
  return upload(soldBaseUrl, record);
}

export function loadSoldTransactionSkus(
  id: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${soldBaseUrl}${id}/skus`, params);
}

export function authoriseSoldTransaction(id: string): Promise<unknown> {
  return get(`${soldBaseUrl}${id}/authorise`);
}

export function exportSoldTransactionData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}sold`, 'creationDate', params);
}

// ===================================
// MOVE
export function getMoveTransactions(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(moveBaseUrl, params);
}

export function getMoveTransactionById(id: string): Promise<MoveTransactionModel> {
  return getSingleRecord<MoveTransactionModel>(moveBaseUrl, id);
}

export function exportMoveTransactionData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}move`, 'creationDate', params);
}

// ===================================
// Ingested
export function getIngestedTransactions(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(ingestedBaseUrl, params);
}

export function getIngestedTransactionById(id: string): Promise<IngestedTransactionModel> {
  return getSingleRecord<IngestedTransactionModel>(ingestedBaseUrl, id);
}

export function removeIngestedKeys(id: string): Promise<unknown> {
  return deleteRecord(ingestedBaseUrl, id);
}

export function retryIngestedKeys(id: string): Promise<unknown> {
  return postAction(`${ingestedBaseUrl}${id}/retry`);
}

export function exportIngestedTransactionsData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}ingested`, 'uploadedDate', params);
}

// ===================================
// Bulk Ingested
export function getBulkIngestedTransactions(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(bulkIngestedBaseUrl, params);
}

export function getBulkIngestedTransactionById(id: string): Promise<unknown> {
  return getSingleRecord(bulkIngestedBaseUrl, id);
}

export function bulkUpload(record: unknown): Promise<unknown> {
  return upload(bulkIngestedBaseUrl, record);
}

export function exportBulkIngestedTransactionsData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}ingested/bulk`, 'uploadedDate', params);
}
// ===================================
// Disable keys
export interface DisableKeysParams {
  keyProviderId?: string;
  reason?: string;
  disabledReasons?: unknown;
  description?: string;
  targetGameId?: string;
  targetGameSKUId?: string;
  targetCompanyId?: string;
  targetKeyWorkflowId?: string;
  targetRequestingUserProfileId?: string;
  targetAccountDetailId?: string;
  targetKeyRequestId?: string;
  targetKeyRequestAccountDetail?: string[];
  targetIsAvailable?: boolean;
  targetIsAllocated?: boolean;
  targetIsActivated?: boolean;
  targetIsSold?: boolean;
  targetIsExpired?: boolean;
}

export function disableKeys(params: DisableKeysParams): Promise<unknown> {
  const {
    keyProviderId,
    reason,
    disabledReasons,
    description,
    targetGameId,
    targetGameSKUId,
    targetCompanyId,
    targetKeyWorkflowId,
    targetRequestingUserProfileId,
    targetAccountDetailId,
    targetKeyRequestId,
    targetKeyRequestAccountDetail,
    targetIsAvailable,
    targetIsAllocated,
    targetIsActivated,
    targetIsSold,
    targetIsExpired,
  } = params;
  return postAction(`${baseUrl}disable`, {
    keyProviderId,
    reason,
    disabledReasons,
    description,

    targetGameId,
    targetGameSKUId,
    targetCompanyId,
    targetKeyWorkflowId,
    targetRequestingUserProfileId,
    targetAccountDetailId,
    targetKeyRequestId,
    targetKeyRequestAccountDetail,

    targetIsAvailable,
    targetIsAllocated,
    targetIsActivated,
    targetIsSold,
    targetIsExpired,
  });
}
// ===================================
// Move keys
export function moveKeys(params: {
  skuId: string;
  sourceKeyWorkflowId?: string;
  destinationKeyPools?: unknown;
  description?: string;
}): Promise<unknown> {
  const { skuId, sourceKeyWorkflowId, destinationKeyPools, description } = params;
  return postAction(`${baseUrl}sku/${skuId}/moveKeys`, {
    sourceKeyWorkflowId,
    destinationKeyPools,
    description,
  });
}

export function splitFileIntoKeyPools(params: {
  skuId: string;
  fileId?: string;
  destinationKeyPools?: unknown;
  description?: string;
}): Promise<unknown> {
  const { skuId, fileId, destinationKeyPools, description } = params;
  return postAction(`${baseUrl}sku/${skuId}/splitFileIntoKeyPools`, {
    fileId,
    destinationKeyPools,
    description,
  });
}

// ===================================
// Move keys between requests
export function moveKeysBetweenRequests(params: {
  sourceKeyRequestId?: string;
  destinationKeyRequestId?: string;
  reason?: string;
}): Promise<unknown> {
  const { sourceKeyRequestId, destinationKeyRequestId, reason } = params;
  return postAction(`${baseUrl}moveKeysBetweenRequests`, {
    sourceKeyRequestId,
    destinationKeyRequestId,
    reason,
  });
}
